import React from 'react';
import { Tag, Typography } from 'antd';
import { PageHeader } from '@ant-design/pro-layout';
import { useFindCountry } from 'shared/hooks/use-find-country.ts';
import type { ClientType } from 'types/client.ts';
import { ClientBlockedAlert } from 'components/clients/client-blocked-alert';
import { ClientCreditInfo } from 'components/clients/client-credit-info';
import { ClientExtraActions } from 'components/clients/client-extra-actions';
import { ClientTypeTag } from 'components/clients/client-type-tag';
import { CreditLimitAlert } from 'components/clients/credit-limit-alert';
import { EditClient } from 'components/clients/edit-client';
import { CreateContact } from 'components/contacts/create-contact';
import styles from './client-page-header.module.scss';
import type { IClientPageHeaderProps } from './types';

export const ClientPageHeader: React.FC<IClientPageHeaderProps> = (props) => {
  const { client } = props;

  const country = useFindCountry(client?.legalCountryId);
  const postCountry = useFindCountry(client?.postCountryId);

  return (
    <PageHeader
      title={client.shortName || client.name}
      extra={[
        <CreateContact key='create-contact' clientId={client.id} />,
        <EditClient key='edit-client' client={client} />,
        <ClientExtraActions
          key='client-extra-actions'
          clientId={client.id}
          isBlocked={client.isBlocked}
          creditLimit={client.creditLimit}
        />,
      ]}
      tags={[
        <ClientTypeTag key='client-type' type={client.type as ClientType} isVip={client.isVip} />,
        <Tag key='payment-terms'>{client.paymentTerms}</Tag>,
        <Tag key='payment-period'>{client.paymentPeriod} days</Tag>,
      ]}
      onBack={() => history.back()}>
      <ClientBlockedAlert isBlocked={client.isBlocked} />
      {!client.isBlocked ? <CreditLimitAlert clientId={client.id} creditLimit={client.creditLimit} /> : null}
      <div className={styles.container}>
        <div>
          <div>{client.name}</div>
          {client.vatNumber ? (
            <div>
              <strong>VAT number:</strong> {client.vatNumber}
            </div>
          ) : null}
          <div>
            <strong>Address: </strong>
            {country ? (
              <Typography.Text italic>
                ({country.code}) {country.name} -{' '}
              </Typography.Text>
            ) : null}
            {client.legalPostCode ? <Typography.Text italic>{client.legalPostCode} - </Typography.Text> : null}
            {client.legalCity ? <Typography.Text italic>{client.legalCity} - </Typography.Text> : null}
            {client.legalAddress ? <Typography.Text italic>{client.legalAddress}</Typography.Text> : null}
          </div>
          <div>
            <strong>Post Address: </strong>
            {postCountry ? (
              <Typography.Text italic>
                ({postCountry.code}) {postCountry.name} -{' '}
              </Typography.Text>
            ) : null}
            {client.postPostCode ? <Typography.Text italic>{client.postPostCode} - </Typography.Text> : null}
            {client.postCity ? <Typography.Text italic>{client.postCity} - </Typography.Text> : null}
            {client.postAddress ? <Typography.Text italic>{client.postAddress}</Typography.Text> : null}
          </div>
          <div>
            <strong>Email for documents:</strong> {client.emailForDocuments}
          </div>
        </div>
        <ClientCreditInfo clientId={client.id} creditLimit={client.creditLimit} />
      </div>
    </PageHeader>
  );
};
